import { z } from "zod"

import type { VaasCourse, VaasLesson } from "~/v2/types/vaas"

const createdBySchema = z
  .enum(["human", "modified-ai", "created-ai"])
  .nullable()
  .optional()

const aiMetadataSchema = {
  created_by: createdBySchema,
  vaas_provider_name: z
    .string()
    .trim()
    .max(255, "Provider name too long")
    .optional()
    .or(z.literal("")),
  vaas_system_version: z
    .string()
    .trim()
    .max(255, "System version too long")
    .optional()
    .or(z.literal("")),
  vaas_creation_timestamp: z.string().trim().optional().or(z.literal("")),
}

const createVaasSchema = z.object({
  course_type: z.literal("vaas"),
  disable_final_assessment: z.literal(true),
  course_name: z
    .string()
    .trim()
    .min(1, "Course name required")
    .max(255, "Course name too long"),
  course_thumbnail: z.any().optional(),
  lesson_description: z
    .string()
    .trim()
    .max(2000, "Lesson description too long"),
  vaas_logo_redirect: z
    .string()
    .trim()
    .max(2000, "VAAS logo redirect too long")
    .or(z.literal("")),
  vaas_show_feedback: z.boolean(),
  ...aiMetadataSchema,
})

const editVaasSchema = z.object({
  disable_final_assessment: z.literal(true),
  course_name: z
    .string()
    .trim()
    .min(1, "Course name required")
    .max(255, "Course name too long"),
  course_thumbnail: z.any().optional(),
  lesson_description: z
    .string()
    .trim()
    .max(2000, "Lesson description too long"),
  vaas_logo_redirect: z
    .string()
    .trim()
    .max(2000, "VAAS logo redirect too long")
    .or(z.literal("")),
  vaas_show_feedback: z.boolean(),
  ...aiMetadataSchema,
})

const formatVaasTimestampForInput = (timestamp?: string | null) =>
  timestamp ? String(timestamp).slice(0, 16) : ""

const buildEditVaasDefaults = ({
  course,
  lesson,
}: {
  course?: VaasCourse | null
  lesson?: VaasLesson | null
}) => ({
  disable_final_assessment: true,
  course_name: course?.name || "",
  course_thumbnail: course?.thumbnail || {},
  created_by: lesson?.created_by || null,
  lesson_description: lesson?.description || "",
  vaas_creation_timestamp: formatVaasTimestampForInput(
    lesson?.vaas_creation_timestamp,
  ),
  vaas_logo_redirect: course?.vaas_logo_redirect || "",
  vaas_provider_name: lesson?.vaas_provider_name || "",
  vaas_show_feedback: Boolean(course?.vaas_show_feedback),
  vaas_system_version: lesson?.vaas_system_version || "",
})

const buildVaasLessonName = (courseName?: string | null) =>
  `${courseName?.trim() || "Untitled VAAS"} - lesson`

export {
  buildEditVaasDefaults,
  buildVaasLessonName,
  createVaasSchema,
  editVaasSchema,
  formatVaasTimestampForInput,
}
